import { TxCenterContactsTable } from '@features/components/CaseOverview/components/TxCenterContactsTable/TxCenterContactsTable';
import {
  ClinicalSpecialistTable,
  DeviceTable,
  SurgeonsTable,
  OMSTable,
  ClinicalCoordinatorsTable,
  ClinicalLeadsTable,
} from '@lp/features/AssignmentTables';
import { ZonedDateTime } from '@internationalized/date';
import { ResourceRowData } from '@features/components/CaseOverview/components/AssignedResources/types';
import { NonLogisticsEmployeesTable } from './Tables/EmployeesTable/NonLogisticsEmployeesTable';
import { OpoContactsTable } from './Tables/OpoContactsTable/OpoContactsTable';
import { EmptyStatePlaceholder } from './EmptyStatePlaceholder';
import { ActiveResourceType, ResourceFilters, SurgeonType } from '../config';
import { Case, CaseResourceCombined, OpoContactWithPrimary } from '@/api/apis/case';
import { Hub } from '@/api/apis/facilities';
import { UserRole } from '@/api/apis/user';
import { EmployeeRole } from '@/api/apis/resources';
import { ResourceWarning } from '../hooks/useResourceWarnings/types';
import { createMatchWarningsHandler } from '../hooks/useResourceWarnings/handlers';

type WithId = {
  id: string;
};

export type AssignmentActions = {
  onUserAssign: (row: WithId) => void;
  onTxAssign: (user: ResourceRowData) => void;
  onOpoContactAssign: (row: WithId) => void;
  onDeviceReserve: (row: WithId) => void;
  onClinicalSpecialistReserve: (id: string) => void;
  onSurgeonReserve: (id: string) => void;
};

export type TimeProps = {
  orDateTime: ZonedDateTime;
  goDateTime: ZonedDateTime;
  applyGoTime: boolean;
};

export type FilterProps = {
  filters: ResourceFilters;
  selectedHub?: Hub;
};

export type DataProps = {
  usersToAssign: Partial<Record<UserRole, string[]>>;
  opoContactsToAssign: OpoContactWithPrimary[];
  newClinicalSpecialistIds: string[];
  newSurgeonIds: string[];
  newDevicesIds: string[];
  ocsConsoles: CaseResourceCombined[];
};

export type ActiveResourceTableProps = {
  resourceType: ActiveResourceType;
  searchTerm: string;
  assignmentActions: AssignmentActions;
  timeProps: TimeProps;
  filterProps: FilterProps;
  resourcesSelection: DataProps;
  caseInfo: Case;
  toggleWarning: (warning: ResourceWarning) => void;
};

export const ActiveResourceTable = ({
  caseInfo,
  resourceType,
  searchTerm,
  assignmentActions,
  timeProps,
  filterProps,
  resourcesSelection,
  toggleWarning,
}: ActiveResourceTableProps) => {
  const { usersToAssign, newClinicalSpecialistIds, newSurgeonIds, newDevicesIds, ocsConsoles, opoContactsToAssign } =
    resourcesSelection;
  const {
    onUserAssign,
    onTxAssign,
    onOpoContactAssign,
    onDeviceReserve,
    onClinicalSpecialistReserve,
    onSurgeonReserve,
  } = assignmentActions;
  const { filters, selectedHub } = filterProps;
  const { orDateTime, goDateTime, applyGoTime } = timeProps;
  const { caseId, organType, donorType, txCenterDetails, opoDetails, trialProgram } = caseInfo;
  const isContractorSurgeon = filters.surgeonType[0] === SurgeonType.Contractor;
  const orDateTimeString = orDateTime.toAbsoluteString();
  const goDateTimeString = goDateTime.toAbsoluteString();

  const updateMatchWarning = createMatchWarningsHandler({
    orTime: orDateTime.toAbsoluteString(),
    goTime: goDateTime.toAbsoluteString(),
    applyGoTime,
    trialProgram,
    toggleWarning,
  });

  switch (resourceType) {
    case ActiveResourceType.TxCenterContact:
      return (
        <TxCenterContactsTable
          searchTerm={searchTerm}
          onUserAssign={onTxAssign}
          usersToAssign={usersToAssign}
          selectedRole={filters.txCenterContact[0]}
          txCenterId={txCenterDetails?.id}
        />
      );
    case ActiveResourceType.TmdxUser:
      return (
        <NonLogisticsEmployeesTable
          searchTerm={searchTerm}
          onTmdxUserAssign={onUserAssign}
          usersToAssign={usersToAssign}
          selectedRole={filters.tmdxUser[0]}
          organType={organType}
        />
      );
    case ActiveResourceType.ClinicalCoordinator:
      return (
        <ClinicalCoordinatorsTable
          searchTerm={searchTerm}
          caseInfo={caseInfo}
          idsToAssign={usersToAssign[EmployeeRole.ClinicalCoordinator]}
          onAssign={onUserAssign}
          toggleWarning={toggleWarning}
        />
      );
    case ActiveResourceType.ClinicalLead:
      return (
        <ClinicalLeadsTable
          searchTerm={searchTerm}
          caseInfo={caseInfo}
          idsToAssign={usersToAssign[EmployeeRole.ClinicalLead]}
          onAssign={onUserAssign}
          toggleWarning={toggleWarning}
        />
      );
    case ActiveResourceType.OrganManagementSpecialist:
      return (
        <OMSTable
          searchTerm={searchTerm}
          caseInfo={caseInfo}
          idsToAssign={usersToAssign[EmployeeRole.OrganManagementSpecialist]}
          toggleWarning={toggleWarning}
          onAssign={onUserAssign}
        />
      );
    case ActiveResourceType.ClinicalSpecialist:
      return (
        <ClinicalSpecialistTable
          searchTerm={searchTerm}
          orTime={orDateTimeString}
          goTime={goDateTimeString}
          applyGoTime={applyGoTime}
          caseId={caseId}
          hubId={selectedHub?.id || ''}
          organ={organType}
          donor={donorType}
          isLogistics={false}
          selectedSpecialistsIds={newClinicalSpecialistIds}
          trialProgramId={trialProgram?.id}
          onReserve={(cs) => {
            updateMatchWarning(cs, 'cs');
            onClinicalSpecialistReserve(cs.employeeId);
          }}
        />
      );
    case ActiveResourceType.RetrievalSurgeon:
      return (
        <SurgeonsTable
          isContractor={isContractorSurgeon}
          affiliation={filters.affiliationType[0]}
          searchTerm={searchTerm}
          orTime={orDateTimeString}
          caseId={caseId}
          organ={organType}
          donor={donorType}
          isLogistics={false}
          selectedSurgeonsIds={newSurgeonIds}
          onContractorReserve={(contractor) => {
            onSurgeonReserve(contractor.employeeId);
          }}
          onEmployeeReserve={(surgeon) => {
            updateMatchWarning(surgeon, 'rs');
            onSurgeonReserve(surgeon.employeeId);
          }}
        />
      );
    case ActiveResourceType.OpoContact:
      return (
        <OpoContactsTable
          opoId={opoDetails?.id}
          searchTerm={searchTerm}
          assignedOpoContacts={opoContactsToAssign}
          onContactAssign={onOpoContactAssign}
        />
      );
    case ActiveResourceType.Device:
      return (
        <DeviceTable
          disableReserve={!!ocsConsoles.length}
          caseId={caseId}
          searchTerm={searchTerm}
          isLogistics={false}
          hubId={selectedHub?.id}
          selectedDeviceId={newDevicesIds[0]}
          assignedDevices={ocsConsoles}
          organType={organType}
          onDeviceAssign={onDeviceReserve}
          trialProgram={trialProgram}
        />
      );
    default:
      return <EmptyStatePlaceholder filters={filters} selectedHub={selectedHub} />;
  }
};
